import { useDispatch, useSelector } from 'react-redux';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as tradeInsActionCreators from '@makemydeal/dr-dash-store/actions/tradeInsActionCreators';
import * as tradeInComponentActionCreators from '@makemydeal/dr-dash-store/actions/tradeInComponentActionCreators';
import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as contractingSelectors from '@makemydeal/dr-dash-store/selectors/contractingSelectors';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';

import { AccordionGroup } from '@interstate/components/AccordionGroup';
import React, { useCallback, useEffect, useState } from 'react';
import { MultiTradeInAccordionGroupContainer } from './multiTradeIn.style';
import { MANAGER_VIEW_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { TradeInAccordion } from './TradeInAccordion';
import { SingleTradeInWrapper } from './SingleTradeInWrapper';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';

type MultiTradeInProps = {
    isEditDisabled: boolean;
    isTradeInFormDirtyRef?: React.MutableRefObject<boolean>;
    onContractingErrorsAlert?: (hasErrors: boolean) => void;
};

const excludeFields = ['acv', 'allowance', 'payoff'];

export const MultiTradeIn = ({ isEditDisabled, isTradeInFormDirtyRef, onContractingErrorsAlert }: MultiTradeInProps) => {
    const dispatch = useDispatch();
    const tradeIns = useSelector(tradeInsSelectors.getAllTradeIns);
    const tradeInErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getCategoryValidationErrors(state, DealTabs.TRADE_IN, DealTabs.TRADE_IN)
    );
    const activeTab = useSelector(baseDealSelectors.getActiveTab);
    const [hasInitializedAccordions, setHasInitializedAccordions] = useState(false);

    const offerIsBeingSaved = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const contractStatus = useSelector(contractingSelectors.getStatus);
    const contractFailureReason = useSelector(contractingSelectors.getFailureReasons) || [];
    const isContractFailed = contractStatus === 'failure' || contractFailureReason.length > 0;

    const defaultOpenIndices = tradeIns.filter(({ isExpanded }) => isExpanded).map((_, index) => index);

    const [tradeInContractErrors, setTradeInContractErrors] = useState<Record<number, boolean>>({});

    const handleTradeInContractError = useCallback((tradeInIndex: number, hasErrors: boolean) => {
        setTradeInContractErrors((prev) => {
            if (prev[tradeInIndex] === hasErrors) return prev;
            return { ...prev, [tradeInIndex]: hasErrors };
        });
    }, []);

    useEffect(() => {
        const hasAnyContractErrors = Object.values(tradeInContractErrors).some(Boolean);
        onContractingErrorsAlert?.(hasAnyContractErrors);
    }, [tradeInContractErrors]);

    const tradeInHasErrors = useCallback(
        (index: number) => {
            if (!tradeInErrors[index] || tradeIns[index].isDeleted) return false;
            const errorObj = tradeInErrors[index];

            return Object.entries(errorObj).some(([key, value]) => {
                if (excludeFields.includes(key.toLowerCase())) {
                    return false;
                }
                return value === true;
            });
        },
        [tradeInErrors, tradeIns]
    );

    const expandTradesWithErrors = useCallback(() => {
        tradeIns.forEach((tradeIn, index) => {
            if (!tradeIn.isDeleted && tradeInHasErrors(index) && !tradeIn.isExpanded) {
                dispatch(tradeInsActionCreators.toggleTradeInAccordionState(index));
            }
        });
    }, [tradeIns, tradeInHasErrors, dispatch]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (!hasInitializedAccordions) {
            timer = setTimeout(() => {
                tradeIns.forEach((tradeIn, index) => {
                    if (!tradeIn.isDeleted && !tradeInHasErrors(index) && tradeIn.isExpanded) {
                        dispatch(tradeInsActionCreators.toggleTradeInAccordionState(index));
                    }
                });
                setHasInitializedAccordions(true);
            }, 0);
        }
        return () => clearTimeout(timer);
    }, [tradeIns, tradeInHasErrors, dispatch, hasInitializedAccordions]);

    useEffect(() => {
        if (activeTab === DealTabs.TRADE_IN) {
            expandTradesWithErrors();
        }
    }, [activeTab]);

    useEffect(() => {
        if ((!offerIsBeingSaved && !offerHasBeenSaved) || isContractFailed) {
            expandTradesWithErrors();
        }
    }, [offerIsBeingSaved, offerHasBeenSaved, contractStatus]);

    useEffect(() => {
        dispatch(tradeInComponentActionCreators.fetchTradeInComponentYears(MANAGER_VIEW_APP_PREFIX));
    }, [dispatch]);

    const renderTradeInAccordion = () => {
        return (
            <MultiTradeInAccordionGroupContainer data-testid="multi-tradein-accordion-group" data-portal-container="autocomplete">
                <AccordionGroup accordionType="multi-expanded" defaultOpen={defaultOpenIndices}>
                    {tradeIns.map(({ isExpanded, isDeleted }, formIndex) => (
                        <TradeInAccordion
                            key={formIndex}
                            formIndex={formIndex}
                            isExpanded={isExpanded || tradeIns.length === 1}
                            isDeleted={isDeleted}
                            isEditDisabled={isEditDisabled}
                            isTradeInFormDirtyRef={isTradeInFormDirtyRef}
                            onContractingErrorsAlert={(hasErrors) => handleTradeInContractError(formIndex, hasErrors)}
                            onToggle={() => {
                                dispatch(interactionAnalyticsActionCreators.tradeInVehicleAccordionSummary());
                                dispatch(tradeInsActionCreators.toggleTradeInAccordionState(formIndex));
                            }}
                        />
                    ))}
                </AccordionGroup>
            </MultiTradeInAccordionGroupContainer>
        );
    };

    if (tradeIns.length === 1) {
        return (
            <SingleTradeInWrapper
                formIndex={0}
                isEditDisabled={isEditDisabled || tradeIns[0].isDeleted}
                isTradeInFormDirtyRef={isTradeInFormDirtyRef}
                onContractingErrorsAlert={(hasErrors) => handleTradeInContractError(0, hasErrors)}
            />
        );
    }

    return renderTradeInAccordion();
};
